import React, { useState, useRef } from 'react';
import { useReviewer } from '../context/ReviewerContext.tsx';
import { reviewService } from '../services/reviewService.ts';
import { useFilters } from '../context/FilterContext.tsx';
import Papa from 'papaparse';

type IdentifierType = 'id' | 'doi' | 'title';

export function ReviewImport() {
    const [file, setFile] = useState<File | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState<any>(null);
    const [selectedReviewer, setSelectedReviewer] = useState<number | null>(null);
    const [mappings, setMappings] = useState({
        id: '',
        doi: '',
        title: '',
        decision: '',
        reason: '',
    });
    const [identifierTypes, setIdentifierTypes] = useState<IdentifierType[]>(['id']);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { currentReviewer } = useReviewer();
    const { lit_filter } = useFilters();
    const [isDryRun, setIsDryRun] = useState(false);
    const [dryRunResults, setDryRunResults] = useState<any>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseCSV(selectedFile);
        }
    };

    const parseCSV = (file: File) => {
        Papa.parse(file, {
            header: true,
            preview: 5, // Parse only first 5 rows for preview
            skipEmptyLines: true,
            complete: (results) => {
                setPreviewData(results.data);
                setHeaders(results.meta.fields || []);

                // Try to auto-detect column mappings
                const headerLower = results.meta.fields?.map((h) => h.toLowerCase()) || [];

                // Auto-detect ID column
                const idIndex = headerLower.findIndex(
                    (h) => h === 'id' || h === 'literature_id' || h === 'paper_id'
                );

                // Auto-detect DOI column
                const doiIndex = headerLower.findIndex((h) => h === 'doi' || h === 'digital_object_identifier');

                // Auto-detect title column
                const titleIndex = headerLower.findIndex((h) => h === 'title' || h === 'paper_title');

                // Auto-detect decision column
                const decisionIndex = headerLower.findIndex(
                    (h) => h === 'decision' || h === 'verdict' || h === 'status'
                );

                // Auto-detect reason column
                const reasonIndex = headerLower.findIndex(
                    (h) => h === 'reason' || h === 'comment' || h === 'notes'
                );

                const newMappings = { ...mappings };
                if (idIndex >= 0) newMappings.id = results.meta.fields?.[idIndex] || '';
                if (doiIndex >= 0) newMappings.doi = results.meta.fields?.[doiIndex] || '';
                if (titleIndex >= 0) newMappings.title = results.meta.fields?.[titleIndex] || '';
                if (decisionIndex >= 0) newMappings.decision = results.meta.fields?.[decisionIndex] || '';
                if (reasonIndex >= 0) newMappings.reason = results.meta.fields?.[reasonIndex] || '';

                setMappings(newMappings);

                // Set identifier types based on detected columns
                const detectedIdentifiers: IdentifierType[] = [];
                if (idIndex >= 0) detectedIdentifiers.push('id');
                if (doiIndex >= 0) detectedIdentifiers.push('doi');
                if (titleIndex >= 0) detectedIdentifiers.push('title');

                if (detectedIdentifiers.length > 0) {
                    setIdentifierTypes(detectedIdentifiers);
                }
            },
            error: (error) => {
                console.error('Error parsing CSV:', error);
                alert('Error parsing CSV file. Please check the format.');
            },
        });
    };

    const toggleIdentifierType = (type: IdentifierType) => {
        if (identifierTypes.includes(type)) {
            // Don't remove if it's the last one
            if (identifierTypes.length > 1) {
                setIdentifierTypes(identifierTypes.filter((t) => t !== type));
            }
        } else {
            setIdentifierTypes([...identifierTypes, type]);
        }
    };

    const handleDryRun = async () => {
        if (!file || !selectedReviewer) {
            alert('Please select a file and reviewer');
            return;
        }

        if (!mappings.decision) {
            alert('Please map the Decision column');
            return;
        }

        // Check if at least one identifier is mapped
        const hasValidIdentifier = identifierTypes.some((type) => !!mappings[type]);
        if (!hasValidIdentifier) {
            alert('Please map at least one identifier column (ID, DOI, or Title)');
            return;
        }

        setIsProcessing(true);
        setIsDryRun(true);

        try {
            // Parse the entire CSV file
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    const reviews = results.data
                        .filter((row: any) => {
                            return Object.values(row).some(
                                (value) => value !== null && value !== undefined && String(value).trim() !== ''
                            );
                        })
                        .map((row: any) => {
                            // Convert decision values to system format
                            let decision = row[mappings.decision]?.toString().toLowerCase();

                            // Map common decision values to system values
                            if (['yes', 'accept', 'accepted', 'include', 'true', '1'].includes(decision)) {
                                decision = 'accepted';
                            } else if (
                                ['no', 'reject', 'rejected', 'exclude', 'false', '0'].includes(decision)
                            ) {
                                decision = 'rejected';
                            } else if (['maybe', 'needs_full_text', 'full_text'].includes(decision)) {
                                decision = 'needs_full_text';
                            }

                            // Create the review object with identifiers
                            const review: any = {
                                reviewer_id: selectedReviewer,
                                decision: decision.toUpperCase(),
                                reason: row[mappings.reason] || '',
                                project_id: lit_filter.project_id,
                                identifiers: {},
                                original_row: row, // Keep the original row for display
                            };

                            // Add the selected identifiers
                            if (identifierTypes.includes('id') && mappings.id && row[mappings.id]) {
                                review.identifiers.id = parseInt(row[mappings.id]);
                            }

                            if (identifierTypes.includes('doi') && mappings.doi && row[mappings.doi]) {
                                review.identifiers.doi = row[mappings.doi];
                            }

                            if (identifierTypes.includes('title') && mappings.title && row[mappings.title]) {
                                review.identifiers.title = row[mappings.title];
                            }

                            return review;
                        })
                        .filter((review) => {
                            // Filter out reviews without any valid identifiers
                            return Object.keys(review.identifiers).length > 0;
                        });

                    // Send reviews to backend for dry run
                    const response = await reviewService.dryRunImportReviews(reviews);
                    setDryRunResults(response);
                    setIsProcessing(false);
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    alert('Error parsing CSV file. Please check the format.');
                    setIsProcessing(false);
                    setIsDryRun(false);
                },
            });
        } catch (error) {
            console.error('Error in dry run:', error);
            alert('Error performing dry run. Please try again.');
            setIsProcessing(false);
            setIsDryRun(false);
        }
    };

    const proceedWithImport = async () => {
        if (!dryRunResults || !dryRunResults.reviews) {
            return;
        }

        setIsProcessing(true);

        try {
            const response = await reviewService.confirmImportReviews(dryRunResults.reviews);
            setResults(response);
            setDryRunResults(null);
            setIsDryRun(false);
            setIsProcessing(false);
        } catch (error) {
            console.error('Error importing reviews:', error);
            alert('Error importing reviews. Please try again.');
            setIsProcessing(false);
        }
    };

    const cancelDryRun = () => {
        setDryRunResults(null);
        setIsDryRun(false);
    };

    const handleImport = async () => {
        if (!file || !selectedReviewer) {
            alert('Please select a file and reviewer');
            return;
        }

        if (!mappings.decision) {
            alert('Please map the Decision column');
            return;
        }

        // Check if at least one identifier is mapped
        const hasValidIdentifier = identifierTypes.some((type) => !!mappings[type]);
        if (!hasValidIdentifier) {
            alert('Please map at least one identifier column (ID, DOI, or Title)');
            return;
        }

        setIsProcessing(true);

        try {
            // Parse the entire CSV file
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    const reviews = results.data
                        .filter((row: any) => {
                            return Object.values(row).some(
                                (value) => value !== null && value !== undefined && String(value).trim() !== ''
                            );
                        })
                        .map((row: any) => {
                            // Convert decision values to system format
                            let decision = row[mappings.decision]?.toString().toLowerCase();

                            // Map common decision values to system values
                            if (['yes', 'accept', 'accepted', 'include', 'true', '1'].includes(decision)) {
                                decision = 'accepted';
                            } else if (
                                ['no', 'reject', 'rejected', 'exclude', 'false', '0'].includes(decision)
                            ) {
                                decision = 'rejected';
                            } else if (['maybe', 'needs_full_text', 'full_text'].includes(decision)) {
                                decision = 'needs_full_text';
                            }

                            // Create the review object with identifiers
                            const review: any = {
                                reviewer_id: selectedReviewer,
                                decision: decision.toUpperCase(),
                                reason: row[mappings.reason] || '',
                                project_id: lit_filter.project_id,
                                identifiers: {},
                            };

                            // Add the selected identifiers
                            if (identifierTypes.includes('id') && mappings.id && row[mappings.id]) {
                                review.identifiers.id = parseInt(row[mappings.id]);
                            }

                            if (identifierTypes.includes('doi') && mappings.doi && row[mappings.doi]) {
                                review.identifiers.doi = row[mappings.doi];
                            }

                            if (identifierTypes.includes('title') && mappings.title && row[mappings.title]) {
                                review.identifiers.title = row[mappings.title];
                            }

                            return review;
                        })
                        .filter((review) => {
                            // Filter out reviews without any valid identifiers
                            return Object.keys(review.identifiers).length > 0;
                        });

                    // Send reviews to backend
                    const response = await reviewService.importReviews(reviews);
                    setResults(response);
                    setIsProcessing(false);
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    alert('Error parsing CSV file. Please check the format.');
                    setIsProcessing(false);
                },
            });
        } catch (error) {
            console.error('Error importing reviews:', error);
            alert('Error importing reviews. Please try again.');
            setIsProcessing(false);
        }
    };

    const resetForm = () => {
        setFile(null);
        setPreviewData([]);
        setHeaders([]);
        setMappings({
            id: '',
            doi: '',
            title: '',
            decision: '',
            reason: '',
        });
        setIdentifierTypes(['id']);
        setResults(null);
        setIsProcessing(false);
        setDryRunResults(null);
        setIsDryRun(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const isIdentifierMapped = (type: IdentifierType) => {
        return identifierTypes.includes(type) && !!mappings[type];
    };

    const atLeastOneIdentifierMapped = () => {
        return identifierTypes.some((type) => !!mappings[type]);
    };

    return (
        <div className="review-import">
            <h2>Import Reviews from CSV</h2>

            {!results && !dryRunResults ? (
                <>
                    <div className="import-section">
                        <h3>1. Select Reviewer</h3>
                        <select
                            value={selectedReviewer || ''}
                            onChange={(e) => setSelectedReviewer(parseInt(e.target.value))}
                            className="reviewer-select">
                            <option value="">Select a reviewer</option>
                            {currentReviewer
                                ?.filter((r) => r.type === 'user')
                                .map((reviewer) => (
                                    <option key={reviewer.id} value={reviewer.id}>
                                        {reviewer.name} ({reviewer.type})
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className="import-section">
                        <h3>2. Upload CSV File</h3>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            className="file-input"
                        />
                        <p className="help-text">
                            The CSV file should contain at minimum: an identifier (ID, DOI, or Title) and
                            decision (accept/reject)
                        </p>
                    </div>

                    {headers.length > 0 && (
                        <>
                            <div className="import-section">
                                <h3>3. Select Identification Method</h3>
                                <p className="help-text">
                                    Choose how to identify literature items (select at least one):
                                </p>

                                <div className="identifier-options">
                                    <label className="identifier-option">
                                        <input
                                            type="checkbox"
                                            checked={identifierTypes.includes('id')}
                                            onChange={() => toggleIdentifierType('id')}
                                        />
                                        Internal ID
                                    </label>
                                    <label className="identifier-option">
                                        <input
                                            type="checkbox"
                                            checked={identifierTypes.includes('doi')}
                                            onChange={() => toggleIdentifierType('doi')}
                                        />
                                        DOI
                                    </label>
                                    <label className="identifier-option">
                                        <input
                                            type="checkbox"
                                            checked={identifierTypes.includes('title')}
                                            onChange={() => toggleIdentifierType('title')}
                                        />
                                        Title
                                    </label>
                                </div>
                            </div>

                            <div className="import-section">
                                <h3>4. Map Columns</h3>
                                <div className="column-mapping">
                                    {identifierTypes.includes('id') && (
                                        <div className="mapping-row">
                                            <label>Internal ID:</label>
                                            <select
                                                value={mappings.id}
                                                onChange={(e) =>
                                                    setMappings({ ...mappings, id: e.target.value })
                                                }>
                                                <option value="">Select column</option>
                                                {headers.map((header) => (
                                                    <option key={header} value={header}>
                                                        {header}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {identifierTypes.includes('doi') && (
                                        <div className="mapping-row">
                                            <label>DOI:</label>
                                            <select
                                                value={mappings.doi}
                                                onChange={(e) =>
                                                    setMappings({ ...mappings, doi: e.target.value })
                                                }>
                                                <option value="">Select column</option>
                                                {headers.map((header) => (
                                                    <option key={header} value={header}>
                                                        {header}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {identifierTypes.includes('title') && (
                                        <div className="mapping-row">
                                            <label>Title:</label>
                                            <select
                                                value={mappings.title}
                                                onChange={(e) =>
                                                    setMappings({ ...mappings, title: e.target.value })
                                                }>
                                                <option value="">Select column</option>
                                                {headers.map((header) => (
                                                    <option key={header} value={header}>
                                                        {header}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div className="mapping-row">
                                        <label>Decision*:</label>
                                        <select
                                            value={mappings.decision}
                                            onChange={(e) =>
                                                setMappings({ ...mappings, decision: e.target.value })
                                            }
                                            required>
                                            <option value="">Select column</option>
                                            {headers.map((header) => (
                                                <option key={header} value={header}>
                                                    {header}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mapping-row">
                                        <label>Reason:</label>
                                        <select
                                            value={mappings.reason}
                                            onChange={(e) =>
                                                setMappings({ ...mappings, reason: e.target.value })
                                            }>
                                            <option value="">Select column (optional)</option>
                                            {headers.map((header) => (
                                                <option key={header} value={header}>
                                                    {header}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {!atLeastOneIdentifierMapped() && (
                                    <p className="warning-text">
                                        Please map at least one identifier column (ID, DOI, or Title)
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    {previewData.length > 0 && (
                        <div className="import-section">
                            <h3>Preview</h3>
                            <div className="preview-table-container">
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            {headers.map((header) => (
                                                <th key={header}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.map((row, index) => (
                                            <tr key={index}>
                                                {headers.map((header) => (
                                                    <td key={header}>{row[header]}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="import-actions">
                        <button
                            onClick={handleDryRun}
                            disabled={
                                !file ||
                                !selectedReviewer ||
                                !mappings.decision ||
                                !atLeastOneIdentifierMapped() ||
                                false
                                // isProcessing
                            }
                            className="dry-run-button">
                            {isProcessing && isDryRun ? 'Processing...' : 'Dry Run (Preview)'}
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={
                                !file ||
                                !selectedReviewer ||
                                !mappings.decision ||
                                !atLeastOneIdentifierMapped() ||
                                isProcessing
                            }
                            className="import-button">
                            {isProcessing && !isDryRun ? 'Processing...' : 'Import Reviews'}
                        </button>
                    </div>
                </>
            ) : dryRunResults ? (
                <div className="dry-run-results">
                    <h3>Dry Run Results</h3>
                    <div className="results-summary">
                        <p>Total reviews: {dryRunResults.total}</p>
                        <p>Will be imported: {dryRunResults.will_import}</p>
                        <p>Will update existing: {dryRunResults.will_update}</p>
                        <p>Not found: {dryRunResults.not_found}</p>
                    </div>

                    {dryRunResults.found_items && dryRunResults.found_items.length > 0 && (
                        <div className="found-items">
                            <h4>Literature Items Found ({dryRunResults.found_items.length})</h4>
                            <div className="preview-table-container">
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            <th>Match Type</th>
                                            <th>ID</th>
                                            <th>Title</th>
                                            <th>DOI</th>
                                            <th>Decision</th>
                                            <th>Reason</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dryRunResults.found_items.map((item: any, index: number) => (
                                            <tr key={index}>
                                                <td>{item.match_type}</td>
                                                <td>{item.literature.id}</td>
                                                <td>{item.literature.title}</td>
                                                <td>{item.literature.doi}</td>
                                                <td
                                                    className={`decision-${item.review.decision.toLowerCase()}`}>
                                                    {item.review.decision}
                                                </td>
                                                <td>{item.review.reason}</td>
                                                <td>{item.existing ? 'Will Update' : 'Will Add'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {dryRunResults.not_found_items && dryRunResults.not_found_items.length > 0 && (
                        <div className="not-found-items">
                            <h4>Literature Items Not Found ({dryRunResults.not_found_items.length})</h4>
                            <div className="preview-table-container">
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            {identifierTypes.includes('id') && mappings.id && <th>ID</th>}
                                            {identifierTypes.includes('doi') && mappings.doi && <th>DOI</th>}
                                            {identifierTypes.includes('title') && mappings.title && (
                                                <th>Title</th>
                                            )}
                                            <th>Decision</th>
                                            <th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dryRunResults.not_found_items.map((item: any, index: number) => (
                                            <tr key={index} className="not-found-row">
                                                {identifierTypes.includes('id') && mappings.id && (
                                                    <td>{item.original_row[mappings.id]}</td>
                                                )}
                                                {identifierTypes.includes('doi') && mappings.doi && (
                                                    <td>{item.original_row[mappings.doi]}</td>
                                                )}
                                                {identifierTypes.includes('title') && mappings.title && (
                                                    <td>{item.original_row[mappings.title]}</td>
                                                )}
                                                <td>{item.decision}</td>
                                                <td>{item.reason}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="dry-run-actions">
                        <button onClick={cancelDryRun} className="cancel-button">
                            Cancel
                        </button>
                        <button onClick={proceedWithImport} disabled={isProcessing} className="proceed-button">
                            {isProcessing ? 'Processing...' : 'Proceed with Import'}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="import-results">
                    <h3>Import Results</h3>
                    <div className="results-summary">
                        <p>Total reviews processed: {results.total}</p>
                        <p>Successfully imported: {results.imported}</p>
                        {results.updated > 0 && <p>Updated existing: {results.updated}</p>}
                        {results.skipped_empty > 0 && <p>Empty rows skipped: {results.skipped_empty}</p>}
                        {results.errors > 0 && <p className="error-text">Errors: {results.errors}</p>}
                        {results.not_found > 0 && (
                            <p className="warning-text">Literature items not found: {results.not_found}</p>
                        )}
                    </div>
                    <button onClick={resetForm} className="reset-button">
                        Import Another File
                    </button>
                </div>
            )}
        </div>
    );
}
